import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * 笔记进度状态
 */
export interface NoteProgressState {
  stage: 'planning' | 'generating' | 'completed'
  current: number
  total: number
  lastUpdateTime: number
}

/**
 * 重绘 Key 类型
 */
type RedrawKey = `${string}-${number}`

/**
 * 配图生成状态 Store
 * 管理智能配图、重绘、进度追踪等状态
 */
interface GenerationState {
  // ========== 智能配图加载状态 ==========
  smartLoading: Record<string, boolean>
  setSmartLoading: (noteId: string, loading: boolean) => void
  clearSmartLoading: (noteId: string) => void

  // ========== 重绘状态 ==========
  redrawPrompt: Record<RedrawKey, string>
  redrawLoading: Record<RedrawKey, boolean>
  setRedrawPrompt: (key: RedrawKey, prompt: string) => void
  setRedrawLoading: (key: RedrawKey, loading: boolean) => void

  // ========== 等待中的笔记 ==========
  waitingNoteIds: string[] // 改为数组以便持久化
  setWaitingNoteIds: (ids: string[]) => void
  addWaitingNoteId: (noteId: string) => void
  removeWaitingNoteId: (noteId: string) => void
  clearWaitingNoteIds: () => void

  // ========== 活跃批次 (用于任务恢复) ==========
  activeBatchIds: string[]
  setActiveBatchIds: (ids: string[]) => void
  clearActiveBatchIds: () => void

  // ========== 进度追踪 ==========
  noteProgress: Record<string, NoteProgressState>
  updateProgress: (
    noteId: string,
    stage: 'planning' | 'generating' | 'completed',
    current: number,
    total: number
  ) => void
  clearProgress: (noteId: string) => void

  // ========== 完成标记 ==========
  recentlyCompletedIds: string[] // 改为数组以便持久化
  markAsCompleted: (noteId: string) => void
  clearCompletedMark: (noteId: string) => void
  initCompletedIds: (projectId: string) => void
  saveCompletedIds: (projectId: string) => void

  // ========== 重置 ==========
  reset: () => void
}

const initialState = {
  smartLoading: {} as Record<string, boolean>,
  redrawPrompt: {} as Record<RedrawKey, string>,
  redrawLoading: {} as Record<RedrawKey, boolean>,
  waitingNoteIds: [] as string[],
  activeBatchIds: [] as string[],
  noteProgress: {} as Record<string, NoteProgressState>,
  recentlyCompletedIds: [] as string[],
}

export const useGenerationStore = create<GenerationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========== 智能配图加载状态 ==========
      setSmartLoading: (noteId: string, loading: boolean) => {
        set((state) => ({
          smartLoading: { ...state.smartLoading, [noteId]: loading },
        }))
      },

      clearSmartLoading: (noteId: string) => {
        set((state) => {
          const next = { ...state.smartLoading }
          delete next[noteId]
          return { smartLoading: next }
        })
      },

      // ========== 重绘状态 ==========
      setRedrawPrompt: (key: RedrawKey, prompt: string) => {
        set((state) => ({
          redrawPrompt: { ...state.redrawPrompt, [key]: prompt },
        }))
      },

      setRedrawLoading: (key: RedrawKey, loading: boolean) => {
        set((state) => ({
          redrawLoading: { ...state.redrawLoading, [key]: loading },
        }))
      },

      // ========== 等待中的笔记 ==========
      setWaitingNoteIds: (ids: string[]) => {
        set({ waitingNoteIds: ids })
      },

      addWaitingNoteId: (noteId: string) => {
        set((state) => ({
          waitingNoteIds: Array.from(new Set([...state.waitingNoteIds, noteId])),
        }))
      },

      removeWaitingNoteId: (noteId: string) => {
        set((state) => ({
          waitingNoteIds: state.waitingNoteIds.filter((id) => id !== noteId),
        }))
      },

      clearWaitingNoteIds: () => {
        set({ waitingNoteIds: [] })
      },

      // ========== 活跃批次 ==========
      setActiveBatchIds: (ids: string[]) => {
        set({ activeBatchIds: ids })
      },

      clearActiveBatchIds: () => {
        set({ activeBatchIds: [] })
      },

      // ========== 进度追踪 ==========
      updateProgress: (
        noteId: string,
        stage: 'planning' | 'generating' | 'completed',
        current: number,
        total: number
      ) => {
        set((state) => ({
          noteProgress: {
            ...state.noteProgress,
            [noteId]: { stage, current, total, lastUpdateTime: Date.now() },
          },
        }))
      },

      clearProgress: (noteId: string) => {
        set((state) => {
          const next = { ...state.noteProgress }
          delete next[noteId]
          return { noteProgress: next }
        })
      },

      // ========== 完成标记 ==========
      markAsCompleted: (noteId: string) => {
        set((state) => ({
          recentlyCompletedIds: Array.from(new Set([...state.recentlyCompletedIds, noteId])),
        }))
      },

      clearCompletedMark: (noteId: string) => {
        set((state) => ({
          recentlyCompletedIds: state.recentlyCompletedIds.filter((id) => id !== noteId),
        }))
      },

      initCompletedIds: (projectId: string) => {
        try {
          const stored = localStorage.getItem(`notes_completed_${projectId}`)
          if (stored) {
            set({ recentlyCompletedIds: JSON.parse(stored) })
          }
        } catch {
          // 忽略解析错误
        }
      },

      saveCompletedIds: (projectId: string) => {
        try {
          const { recentlyCompletedIds } = get()
          localStorage.setItem(
            `notes_completed_${projectId}`,
            JSON.stringify(recentlyCompletedIds)
          )
        } catch (err) {
          console.error('Failed to save completed IDs:', err)
        }
      },

      // ========== 重置 ==========
      reset: () => {
        set({
          smartLoading: {},
          redrawPrompt: {},
          redrawLoading: {},
          waitingNoteIds: [],
          activeBatchIds: [],
          noteProgress: {},
          // 不重置 recentlyCompletedIds，它需要持久化
        })
      },
    }),
    {
      name: 'rednote-generation-storage',
      storage: createJSONStorage(() => localStorage),
      // 仅持久化关键状态
      partialize: (state) => ({
        smartLoading: state.smartLoading,
        activeBatchIds: state.activeBatchIds,
        noteProgress: state.noteProgress,
        waitingNoteIds: state.waitingNoteIds,
        recentlyCompletedIds: state.recentlyCompletedIds,
      }),
    }
  )
)

// ========== 辅助 Hooks ==========

/**
 * 检查笔记是否正在智能配图
 */
export function useIsNoteLoading(noteId: string): boolean {
  return useGenerationStore((state) => state.smartLoading[noteId] || false)
}

/**
 * 获取笔记进度
 */
export function useNoteProgress(noteId: string): NoteProgressState | undefined {
  return useGenerationStore((state) => state.noteProgress[noteId])
}

/**
 * 检查笔记是否在等待中
 */
export function useIsNoteWaiting(noteId: string): boolean {
  return useGenerationStore((state) => state.waitingNoteIds.includes(noteId))
}

/**
 * 检查笔记是否最近完成
 */
export function useIsNoteRecentlyCompleted(noteId: string): boolean {
  return useGenerationStore((state) => state.recentlyCompletedIds.includes(noteId))
}
